import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguageCurrency, formatPhoneNumber } from '../context/LanguageCurrencyContext';

const Footer = () => {
  const { t } = useTranslation();
  const { language } = useLanguageCurrency();
  const isRTL = language === 'ar';
  
  return (
    <footer className="relative overflow-hidden" style={{ backgroundColor: '#1D2A3B', direction: isRTL ? 'rtl' : 'ltr' }}>
      {/* Background Design Elements */}
      <div className="absolute inset-0">
        {/* Gradient mesh blurs */}
        <div 
          className="absolute top-0 left-1/4 w-96 h-96 rounded-full blur-3xl opacity-30"
          style={{ 
            background: 'radial-gradient(circle, rgba(213, 181, 119, 0.5) 0%, transparent 70%)'
          }}
        ></div>
        <div 
          className="absolute bottom-0 right-1/4 w-96 h-96 rounded-full blur-3xl opacity-25"
          style={{ 
            background: 'radial-gradient(circle, rgba(213, 181, 119, 0.4) 0%, transparent 70%)'
          }}
        ></div>
        <div 
          className="absolute top-1/2 right-0 w-80 h-80 rounded-full blur-3xl opacity-20"
          style={{ 
            background: 'radial-gradient(circle, rgba(213, 181, 119, 0.3) 0%, transparent 70%)'
          }}
        ></div>
        <div 
          className="absolute bottom-1/3 left-0 w-72 h-72 rounded-full blur-3xl opacity-15"
          style={{ 
            background: 'radial-gradient(circle, rgba(213, 181, 119, 0.35) 0%, transparent 70%)'
          }}
        ></div>
        
        {/* Grid pattern */}
        <div 
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: 'linear-gradient(90deg, rgba(213, 181, 119, 0.1) 1px, transparent 1px), linear-gradient(rgba(213, 181, 119, 0.1) 1px, transparent 1px)',
            backgroundSize: '50px 50px'
          }}
        ></div>
        
        {/* Diagonal stripes */}
        <div 
          className="absolute top-0 left-0 w-full h-full opacity-[0.02]"
          style={{
            background: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(213, 181, 119, 0.1) 10px, rgba(213, 181, 119, 0.1) 20px)'
          }}
        ></div>
      </div>

      {/* Content */}
      <div className="relative z-10 text-gray-300 px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8 sm:mb-12">
            {/* All sections side by side */}
            <div className="flex flex-col md:flex-row gap-8 lg:gap-12 xl:gap-16">
              {/* HOSTA GLOBAL Section */}
              <div className="w-full md:w-auto md:flex-1 md:max-w-xs lg:max-w-sm">
                <Link to="/" className="flex items-center space-x-2 sm:space-x-3 mb-4 group">
                  {/* Logo */}
                  <div className="flex items-center gap-2 sm:gap-2.5" style={{ flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                    <div className="flex items-center justify-center p-0.5 sm:p-1 rounded" style={{ backgroundColor: '#FFFFFF' }}>
                      <img 
                        src="/hg-logo.png" 
                        alt="HOSTA GLOBAL Logo" 
                        className="w-auto flex-shrink-0"
                        style={{ maxHeight: '20px', objectFit: 'contain' }}
                      />
                    </div>
                    <div className="flex flex-col">
                      <div className="flex items-baseline gap-1" style={{ direction: 'ltr' }}>
                        <span className="text-lg sm:text-xl font-bold leading-tight uppercase" style={{ color: '#D5B577', fontFamily: 'serif' }}>HOSTA</span>
                        <span className="text-lg sm:text-xl font-bold leading-tight uppercase" style={{ color: '#D5B577', fontFamily: 'serif' }}>GLOBAL</span>
                      </div>
                      <span className="text-[10px] leading-tight uppercase tracking-wide" style={{ color: '#D5B577', opacity: 0.8, direction: 'ltr' }}>ELEVATING ASSETS | REDEFINING STAYS</span>
                    </div>
                  </div>
                </Link>
                <p className="text-sm sm:text-base text-gray-400 leading-relaxed">
                  {t('footer.description')}
                </p>
              </div>

              {/* Company Section */}
              <div className="w-full md:w-auto md:flex-1 md:max-w-xs lg:max-w-sm">
                <h4 className="text-white font-semibold mb-3 sm:mb-4 text-base sm:text-lg">{t('footer.company')}</h4>
                <ul className="space-y-2">
                  <li>
                    <Link
                      to="/"
                      className="text-sm sm:text-base hover:text-[#D5B577] transition-colors duration-200 block"
                    >
                      {t('nav.home')}
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/properties"
                      className="text-sm sm:text-base hover:text-[#D5B577] transition-colors duration-200 block"
                    >
                      {t('nav.properties')}
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/exclusive-offers"
                      className="text-sm sm:text-base hover:text-[#D5B577] transition-colors duration-200 block"
                    >
                      {t('nav.exclusiveOffers')}
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/about"
                      className="text-sm sm:text-base hover:text-[#D5B577] transition-colors duration-200 block"
                    >
                      {t('nav.aboutUs')}
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/touring-guide"
                      className="text-sm sm:text-base hover:text-[#D5B577] transition-colors duration-200 block"
                    >
                      {t('nav.touringGuide')}
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/contact"
                      className="text-sm sm:text-base hover:text-[#D5B577] transition-colors duration-200 block"
                    >
                      {t('nav.contact')}
                    </Link>
                  </li>
                 
                </ul>
              </div>

              {/* Contact Information Section */}
              <div className="w-full md:w-auto md:flex-1 md:max-w-xs lg:max-w-sm">
              <h4 className="text-white font-semibold mb-3 sm:mb-4 text-base sm:text-lg">{t('footer.contact')}</h4>
              <ul className="space-y-2 mb-4 sm:mb-6">
                <li className="flex items-start sm:items-center gap-2">
                  <svg
                    className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 mt-0.5 sm:mt-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    style={{ color: '#D5B577' }}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                  <span className="text-xs sm:text-sm md:text-base leading-tight sm:leading-normal">
                    {t('contact.address')}
                  </span>
                </li>
                <li className="flex items-start sm:items-center gap-2">
                  <svg
                    className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 mt-0.5 sm:mt-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    style={{ color: '#D5B577' }}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                  </svg>
                  <a
                    href="mailto:support@hostaglobal.com"
                    className="text-xs sm:text-sm md:text-base hover:text-[#D5B577] transition-colors duration-200 break-words leading-tight sm:leading-normal"
                  >
                    support@hostaglobal.com
                  </a>
                </li>
                <li className="flex items-start sm:items-center gap-2">
                  <svg
                    className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 mt-0.5 sm:mt-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    style={{ color: '#D5B577' }}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                    />
                  </svg>
                  <a
                    href="tel:+966568885544"
                    className="text-xs sm:text-sm md:text-base hover:text-[#D5B577] transition-colors duration-200 leading-tight sm:leading-normal"
                  >
                    {formatPhoneNumber('+966 568885544', language)}
                  </a>
                </li>
              </ul>
              
              {/* Social Media Links */}
              <div>
                <h4 className="text-white font-semibold mb-3 sm:mb-4 text-base sm:text-lg">{t('footer.followUs')}</h4>
                <div className="flex gap-2 sm:gap-3 flex-wrap">
                  <a
                    href="#"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center transition-all duration-300 transform hover:scale-110"
                    style={{ backgroundColor: 'rgba(213, 181, 119, 0.15)' }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'rgba(213, 181, 119, 0.3)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(213, 181, 119, 0.3)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'rgba(213, 181, 119, 0.15)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    <svg className="w-4 h-4 sm:w-5 sm:h-5" style={{ color: '#D5B577' }} fill="currentColor" viewBox="0 0 24 24">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                    </svg>
                  </a>
                  <a
                    href="#"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center transition-all duration-300 transform hover:scale-110"
                    style={{ backgroundColor: 'rgba(213, 181, 119, 0.15)' }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'rgba(213, 181, 119, 0.3)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(213, 181, 119, 0.3)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'rgba(213, 181, 119, 0.15)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    <svg className="w-4 h-4 sm:w-5 sm:h-5" style={{ color: '#D5B577' }} fill="currentColor" viewBox="0 0 24 24">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                    </svg>
                  </a>
                  <a
                    href="#"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center transition-all duration-300 transform hover:scale-110"
                    style={{ backgroundColor: 'rgba(213, 181, 119, 0.15)' }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'rgba(213, 181, 119, 0.3)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(213, 181, 119, 0.3)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'rgba(213, 181, 119, 0.15)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    <svg className="w-4 h-4 sm:w-5 sm:h-5" style={{ color: '#D5B577' }} fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 0C8.74 0 8.333.015 7.053.072 5.775.132 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.012 8.333 0 8.74 0 12s.015 3.667.072 4.947c.06 1.277.261 2.148.558 2.913.306.788.717 1.459 1.384 2.126.667.666 1.336 1.079 2.126 1.384.766.296 1.636.499 2.913.558C8.333 23.988 8.74 24 12 24s3.667-.015 4.947-.072c1.277-.06 2.148-.262 2.913-.558.788-.306 1.459-.718 2.126-1.384.666-.667 1.079-1.335 1.384-2.126.296-.765.499-1.636.558-2.913.06-1.28.072-1.687.072-4.947s-.015-3.667-.072-4.947c-.06-1.277-.262-2.149-.558-2.913-.306-.789-.718-1.459-1.384-2.126C21.319 1.347 20.651.935 19.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.012 15.26 0 12 0zm0 2.16c3.203 0 3.585.016 4.85.071 1.17.055 1.805.249 2.227.415.562.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.015 3.585-.074 4.85c-.061 1.17-.256 1.805-.421 2.227-.224.562-.479.96-.899 1.382-.419.419-.824.679-1.38.896-.42.164-1.065.36-2.235.413-1.274.057-1.649.07-4.859.07-3.211 0-3.586-.015-4.859-.074-1.171-.061-1.816-.256-2.236-.421-.569-.224-.96-.479-1.379-.899-.421-.419-.69-.824-.9-1.38-.165-.42-.359-1.065-.42-2.235-.057-1.274-.07-1.649-.07-4.844 0-3.196.016-3.586.07-4.861.061-1.17.255-1.814.42-2.234.21-.57.479-.96.9-1.381.419-.419.81-.689 1.379-.898.42-.166 1.051-.361 2.221-.421 1.275-.045 1.65-.06 4.859-.06l.045.03zm0 3.678c-3.405 0-6.162 2.76-6.162 6.162 0 3.405 2.76 6.162 6.162 6.162 3.405 0 6.162-2.76 6.162-6.162 0-3.405-2.76-6.162-6.162-6.162zM12 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm7.846-10.405c0 .795-.646 1.44-1.44 1.44-.795 0-1.44-.646-1.44-1.44 0-.794.646-1.439 1.44-1.439.793-.001 1.44.645 1.44 1.439z" />
                    </svg>
                  </a>
                  <a
                    href="#"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center transition-all duration-300 transform hover:scale-110"
                    style={{ backgroundColor: 'rgba(213, 181, 119, 0.15)' }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'rgba(213, 181, 119, 0.3)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(213, 181, 119, 0.3)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'rgba(213, 181, 119, 0.15)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    <svg className="w-4 h-4 sm:w-5 sm:h-5" style={{ color: '#D5B577' }} fill="currentColor" viewBox="0 0 24 24">
                      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
                    </svg>
                  </a>
                </div>
              </div>
            </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-gray-800 pt-6 sm:pt-8 text-xs sm:text-sm text-gray-500">
            <div className="flex justify-center items-center flex-wrap gap-3 sm:gap-4 md:gap-6 mb-4 sm:mb-6">
              <Link
                to="/terms"
                className="hover:text-[#D5B577] transition-colors duration-200 text-center"
              >
                {t('common.termsConditions')}
              </Link>
              <span className="text-gray-600 hidden sm:inline">|</span>
              <a
                href="/privacy"
                className="hover:text-[#D5B577] transition-colors duration-200 text-center"
              >
                {t('common.privacyPolicy')}
              </a>
              <span className="text-gray-600 hidden sm:inline">|</span>
              <a
                href="/refund"
                className="hover:text-[#D5B577] transition-colors duration-200 text-center"
              >
                {t('footer.refundPolicy')}
              </a>
            </div>
            <div className="text-center">
              <p className="text-xs sm:text-sm" style={{ direction: 'ltr' }}>© {new Date().getFullYear()} HOSTA GLOBAL. {t('footer.allRightsReserved')}</p>
              <p className="mt-2 text-xs sm:text-sm">
                {t('footer.developedBy')} <span style={{ color: '#D5B577' }}>syncodeg</span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
